import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2, ArrowRight, Pill } from 'lucide-react';
import { useAuth } from '../api';

const REVIEWS = [
  {
    text: "Antropia transformó la manera en que gestiono mis consultas. El análisis de voz con IA me ahorra horas cada semana.",
    author: "María González",
    role: "Nutricionista deportiva",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=80&h=80&fit=crop"
  },
  {
    text: "Mis clientes aman ver sus gráficas de progreso. La visualización de datos es increíble y muy motivadora.",
    author: "Carlos Fernández",
    role: "Coach de salud",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=80&h=80&fit=crop"
  },
  {
    text: "Las fórmulas de composición corporal son precisas. JP7, Yuhasz, todo calculado automáticamente.",
    author: "Ana Martínez",
    role: "Nutricionista clínica",
    image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=80&h=80&fit=crop"
  },
  {
    text: "El resumen AI de las consultas captura perfectamente los puntos clave. Es como tener un asistente personal.",
    author: "Roberto Silva",
    role: "Entrenador personal",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop"
  },
  {
    text: "Antes tardaba 30 minutos documentando cada consulta. Ahora Antropia lo hace en segundos.",
    author: "Laura Ramírez",
    role: "Dietista registrada",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop"
  },
  {
    text: "La vista pública para mis clientes es perfecta. Pueden ver su progreso sin necesidad de cuenta.",
    author: "Diego Hernández",
    role: "Coach online",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop"
  },
  {
    text: "El sistema multi-tenant es excelente. Cada cliente tiene sus datos completamente aislados y seguros.",
    author: "Patricia López",
    role: "Nutricionista deportiva",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop"
  },
  {
    text: "Las Vitaminas (créditos AI) son muy económicas. Puedo hacer decenas de consultas por muy poco.",
    author: "Javier Torres",
    role: "Coach de wellness",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop"
  },
  {
    text: "El diseño es hermoso y profesional. Mis clientes quedan impresionados cuando les muestro sus datos.",
    author: "Carmen Ruiz",
    role: "Nutricionista holística",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=80&h=80&fit=crop"
  },
  {
    text: "Antropia me ayudó a escalar mi negocio de 20 a 100 clientes sin perder calidad en el servicio.",
    author: "Miguel Ángel Soto",
    role: "Fundador de clínica",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop"
  }
];

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-7212812/pexels-photo-7212812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=940&h=650&fit=crop",
  "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=940&h=650&fit=crop",
  "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=940&h=650&fit=crop",
  "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=940&h=650&fit=crop"
];

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentReview, setCurrentReview] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);
  const { login, register } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    specialty: '',
  });

  // Rotate reviews every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % REVIEWS.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Rotate images every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.name, formData.specialty);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const review = REVIEWS[currentReview];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16" style={{ backgroundColor: 'var(--bg-main)' }}>
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-gradient-to-br from-[#6B8E7D] to-[#8AA894]">
                <Pill size={28} className="text-white" />
              </div>
              <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                Antropia
              </h1>
            </div>
            <p style={{ color: 'var(--text-muted)' }}>
              Antropometría + Inteligencia Artificial
            </p>
          </div>

          {/* Toggle */}
          <div className="flex gap-4 mb-8">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`text-lg font-medium transition-colors ${
                isLogin ? 'text-[#6B8E7D]' : 'text-stone-400 hover:text-stone-600'
              }`}
            >
              Iniciar sesión
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`text-lg font-medium transition-colors ${
                !isLogin ? 'text-[#6B8E7D]' : 'text-stone-400 hover:text-stone-600'
              }`}
            >
              Crear cuenta
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div className="animate-fade-in">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Nombre completo
                  </label>
                  <input
                    data-testid="register-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-organic"
                    placeholder="Dr. Juan García"
                    required={!isLogin}
                  />
                </div>

                <div className="animate-fade-in stagger-1">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Especialidad
                  </label>
                  <input
                    data-testid="register-specialty-input"
                    type="text"
                    value={formData.specialty}
                    onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                    className="input-organic"
                    placeholder="Nutrición deportiva"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Correo electrónico
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-organic"
                placeholder="coach@ejemplo.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Contraseña
              </label>
              <div className="relative">
                <input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-organic pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm animate-fade-in" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 py-4 text-base"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Iniciar sesión' : 'Crear cuenta'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Features */}
          <div className="mt-12 pt-8 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>
              Incluido en tu cuenta
            </p>
            <ul className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8E7D]" />
                Gestión de clientes ilimitada
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8E7D]" />
                Cálculos de composición corporal
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" />
                1000 Vitaminas gratis para AI
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right Panel - Image + Reviews */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative transition-all duration-1000"
        style={{
          backgroundImage: `url(${HERO_IMAGES[currentImage]})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B8E7D]/30 to-black/40" />
        
        {/* Review Card */}
        <div className="absolute bottom-12 left-12 right-12">
          <div 
            key={currentReview} 
            className="glass rounded-2xl p-8 animate-fade-in"
          >
            <p className="text-xl font-medium leading-relaxed mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C221F' }}>
              "{review.text}"
            </p>
            <div className="flex items-center gap-4">
              <img 
                src={review.image}
                alt={review.author}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="font-medium" style={{ color: '#1C221F' }}>{review.author}</p>
                <p className="text-sm" style={{ color: '#57605B' }}>{review.role}</p>
              </div>
            </div>
          </div>
          
          {/* Review indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {REVIEWS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentReview(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentReview ? 'w-6 bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
